import { 
  Home, 
  Phone, 
  User, 
  HelpCircle, 
  Users, 
  BarChart2, 
  Settings 
} from 'lucide-react';
import { NavItem } from '../types/navigation';
import { UserRole, RoleSettings, hasRequiredAccess, DEFAULT_ROLE_SETTINGS } from '../types/shared';
import { NAVIGATION_CONFIG } from '../../components/layout/constants';
import { APP_ROUTES } from '../types/navigation';

export function createNavItem(item: Partial<NavItem>): NavItem {
  return {
    id: item.id || '',
    label: item.label || '',
    icon: item.icon || '',
    path: item.path || '',
    requiredRole: item.requiredRole,
    href: item.href,
    badge: item.badge
  };
}

export function generateNavigation(userRole: UserRole, roleSettings: RoleSettings): NavItem[] {
  // For guest users, return minimal navigation
  if (userRole === 'guest') {
    return NAVIGATION_CONFIG['guest'];
  }

  // Return navigation based on role configuration
  return NAVIGATION_CONFIG[userRole];
}

export function getNavigationForRole(userRole: UserRole): NavItem[] {
  const roleSettings = DEFAULT_ROLE_SETTINGS[userRole];
  return generateNavigation(userRole, roleSettings);
}

export function hasRouteAccess(userRole: UserRole, route: string): boolean {
  // Guest users have very limited access
  if (userRole === 'guest') {
    return route === '/login';
  }

  const navigation = getNavigationForRole(userRole);
  return navigation.some(item => 
    item.path === route || 
    item.children?.some(child => child.path === route)
  );
}

export function getRequiredRole(route: string): UserRole | null {
  const matchedRoute = Object.values(APP_ROUTES).find(r => r.path === route);
  return matchedRoute ? matchedRoute.requiredRole : null;
}

export function getBreadcrumbs(route: string): { label: string; path: string }[] {
  const matchedRoute = Object.values(APP_ROUTES).find(r => r.path === route);
  
  if (!matchedRoute) {
    return [{ label: 'Dashboard', path: '/dashboard' }];
  }

  // Base breadcrumb always includes dashboard
  const baseBreadcrumb = [{ label: 'Dashboard', path: '/dashboard' }];

  // Add current route to breadcrumbs
  return [
    ...baseBreadcrumb,
    { label: matchedRoute.label, path: matchedRoute.path }
  ];
}

export function getFallbackRoute(userRole: UserRole): string {
  // Explicitly handle guest role
  if (userRole === 'guest') {
    return '/login';
  }

  // Fallback to dashboard for all authenticated roles
  return '/dashboard';
}

export function hasPermission(userRole: UserRole, requiredRole: UserRole): boolean {
  return hasRequiredAccess(userRole, requiredRole);
}
